import { flow } from 'fp-ts/function';
import { defaultTo, find, head, isEmpty, reject } from 'lodash/fp';
import { useCallback } from 'react';
import { useTextLocalizationInfo } from '@mews-framework/globalization';
import type { LocalizedPropertyObject } from '../../legacy/types';

/**
 * Hook for localizing translatable properties.
 *
 * A LocalizedProperty is a record of strings, keyed by languages. It's used for translating
 * values like names, descriptions, etc. The values are provided by customers.
 *
 * Note: `textLocalizationInfo` is taken as parameter until it's migrated into context. To provide it,
 * use `getTextLocalizationInfo` selector.
 *
 * @param property - A property to localize
 * @param textLocalizationInfo
 * @returns Localized string as long as the property exists; undefined otherwise
 */
export const useLocalizeProperty = () => {
    const textLocalizationInfo = useTextLocalizationInfo();

    return useCallback(
        (property: LocalizedPropertyObject | null | undefined) => {
            if (!property) {
                return '';
            }

            const { languageCodes } = textLocalizationInfo;
            const languageCode = find((lCode: string) => !!property[lCode])(languageCodes);

            if (languageCode) {
                return property[languageCode];
            }

            return flow(reject(isEmpty), head, defaultTo(''))(property);
        },
        [textLocalizationInfo]
    );
};
